import { __assign, __read, __rest, __spread } from "tslib";
import React, { useMemo, useCallback, useRef, useState, useEffect, } from "react";
import classNames from "classnames";
import { Icon } from "../icon";
import { Popover, } from "../popover";
import { createRocket } from "../_util/create-rocket";
import { useOutsideClick } from "../_util/use-outside-click";
import { forwardRefWithStatics } from "../_util/forward-ref-with-statics";
import { useDefault } from "../_util/use-default";
import { useConfig } from "../_util/config-context";
import { KeyMap } from "../_util/key-map";
import { mergeRefs } from "../_util/merge-refs";
import { noop } from "../_util/noop";
import { getARIAProps } from "../_util/get-aria-props";
import { useScheduleUpdate } from "../_util/use-schedule-update";
import { mergeEventProps } from "../_util/merge-event-props";
import { isMobile } from "../_util/is-mobile";
var getAppearanceConfig = function (classPrefix) { return ({
    // appearance  headerClassName         icon
    default: [classPrefix + "-dropdown-default", "arrowdown"],
    button: [classPrefix + "-dropdown-btn", "arrowdown"],
    link: [classPrefix + "-dropdown-link", "arrowdown"],
    filter: [classPrefix + "-dropdown-filter", "filter"],
    // 兼容原有 raw 类型
    raw: [null, null],
}); };
export var DropdownBox = React.forwardRef(function DropdownBox(_a, ref) {
    var _b;
    var className = _a.className, _c = _a.style, style = _c === void 0 ? {} : _c, children = _a.children, adaptable = _a.adaptable, footer = _a.footer, preventMouseDown = _a.preventMouseDown, props = __rest(_a, ["className", "style", "children", "adaptable", "footer", "preventMouseDown"]);
    var classPrefix = useConfig().classPrefix;
    if (!children) {
        return null;
    }
    return (React.createElement("div", __assign({ ref: ref, className: classNames(classPrefix + "-dropdown-box", (_b = {}, _b[classPrefix + "-dropdown-box--adapte"] = adaptable, _b), className), style: __assign({ position: "relative" }, style), 
        // 阻止点击时触发按钮 onBlur
        onMouseDown: function (event) {
            if (preventMouseDown) {
                event.preventDefault();
            }
        } }, props),
        children,
        footer && React.createElement(Dropdown.Footer, null, footer)));
});
DropdownBox.displayName = "DropdownBox";
var ClickTrigger = function (_a) {
    var childrenElementRef = _a.childrenElementRef, overlayElementRef = _a.overlayElementRef, visible = _a.visible, setVisible = _a.setVisible, _b = _a.openDelay, openDelay = _b === void 0 ? 0 : _b, _c = _a.closeDelay, closeDelay = _c === void 0 ? 0 : _c, render = _a.render, onClose = _a.onClose, onOpen = _a.onOpen;
    var _d = useOutsideClick([
        childrenElementRef,
        overlayElementRef,
    ]), listen = _d.listen, ignoreProps = _d.ignoreProps;
    listen(function () {
        if (visible) {
            onClose();
            setVisible(false, closeDelay);
        }
    });
    return render({
        overlayProps: ignoreProps,
        childrenProps: {},
    });
};
ClickTrigger.displayName = "DropdownClickTrigger";
var HoverTrigger = function (props) {
    if (isMobile) {
        return React.createElement(ClickTrigger, __assign({}, props));
    }
    var visible = props.visible, setVisible = props.setVisible, _a = props.openDelay, openDelay = _a === void 0 ? 50 : _a, _b = props.closeDelay, closeDelay = _b === void 0 ? 100 : _b, render = props.render, onClose = props.onClose, onOpen = props.onOpen;
    var commonProps = {
        onMouseEnter: function () {
            setVisible(true, openDelay).then(function (done) { return done && !visible && onOpen(); });
        },
        onMouseLeave: function () {
            setVisible(false, closeDelay).then(function (done) { return done && onClose(); });
        },
    };
    return render({
        overlayProps: commonProps,
        childrenProps: commonProps,
    });
};
HoverTrigger.displayName = "DropdownHoverTrigger";
export var Dropdown = forwardRefWithStatics(function (props, ref) {
    var _a;
    var classPrefix = useConfig().classPrefix;
    var _b = props.defaultOpen, defaultOpen = _b === void 0 ? false : _b, open = props.open, onOpenChange = props.onOpenChange, appearence = props.appearence, _c = props.appearance, appearance = _c === void 0 ? appearence : _c, button = props.button, size = props.size, _d = props.placement, placement = _d === void 0 ? "bottom-start" : _d, _e = props.placementOffset, placementOffset = _e === void 0 ? 5 : _e, _f = props.trigger, trigger = _f === void 0 ? "click" : _f, children = props.children, footer = props.footer, disabled = props.disabled, _g = props.onOpen, onOpen = _g === void 0 ? noop : _g, _h = props.onClose, onClose = _h === void 0 ? noop : _h, _j = props.onKeyDown, onKeyDown = _j === void 0 ? noop : _j, _k = props.onFocus, onFocus = _k === void 0 ? noop : _k, _l = props.onBlur, onBlur = _l === void 0 ? noop : _l, preventMouseDown = props.preventMouseDown, _m = props.clickClose, clickClose = _m === void 0 ? true : _m, _o = props.closeOnScroll, closeOnScroll = _o === void 0 ? true : _o, escapeWithReference = props.escapeWithReference, popupContainer = props.popupContainer, destroyOnClose = props.destroyOnClose, style = props.style, className = props.className, boxClassName = props.boxClassName, boxSizeSync = props.boxSizeSync, _p = props.matchButtonWidth, matchButtonWidth = _p === void 0 ? boxSizeSync : _p, boxAdaptable = props.boxAdaptable, updateOnChildrenChange = props.updateOnChildrenChange, _q = props.updateDeps, updateDeps = _q === void 0 ? [] : _q, openDelay = props.openDelay, closeDelay = props.closeDelay, overlayClassName = props.overlayClassName, overlayStyle = props.overlayStyle, clearable = props.clearable, _r = props.onClear, onClear = _r === void 0 ? noop : _r, flipped = props.flipped, positionFixed = props.positionFixed, restProps = __rest(props, ["defaultOpen", "open", "onOpenChange", "appearence", "appearance", "button", "size", "placement", "placementOffset", "trigger", "children", "footer", "disabled", "onOpen", "onClose", "onKeyDown", "onFocus", "onBlur", "preventMouseDown", "clickClose", "closeOnScroll", "escapeWithReference", "popupContainer", "destroyOnClose", "style", "className", "boxClassName", "boxSizeSync", "matchButtonWidth", "boxAdaptable", "updateOnChildrenChange", "updateDeps", "openDelay", "closeDelay", "overlayClassName", "overlayStyle", "clearable", "onClear", "flipped", "positionFixed"]);
    var _s = props.boxStyle, boxStyle = _s === void 0 ? {} : _s;
    var _t = __read(useState(false), 2), hover = _t[0], setHover = _t[1];
    var _u = __read(useDefault(open, defaultOpen, onOpenChange), 2), isOpened = _u[0], setIsOpened = _u[1];
    // 是否打开一段时间，防止连续 focus/click 连续两次触发开启导致关闭
    var _v = __read(useState(false), 2), stayedOpen = _v[0], setStayedOpen = _v[1];
    useEffect(function () {
        if (isOpened) {
            var timer_1 = setTimeout(function () { return setStayedOpen(true); }, 150);
            return function () {
                clearTimeout(timer_1);
            };
        }
        setStayedOpen(false);
        return noop;
    }, [isOpened]);
    var dropdownRef = useRef(null);
    var _w = __read(useState(null), 2), dropdownWidth = _w[0], setDropdownWidth = _w[1];
    var close = useCallback(function () {
        setIsOpened(false);
        onClose();
    }, [onClose, setIsOpened]);
    var appearanceConfig = getAppearanceConfig(classPrefix);
    var _x = __read(appearanceConfig[appearance] || appearanceConfig.default, 2), headerClassName = _x[0], icon = _x[1];
    var boxChildren = useMemo(function () {
        return typeof children === "function"
            ? children.call(null, close)
            : children;
    }, [children, close]);
    useEffect(function () {
        if (dropdownRef.current) {
            setDropdownWidth(dropdownRef.current.clientWidth);
        }
    }, [button, size]);
    if (matchButtonWidth && dropdownWidth) {
        boxStyle = __assign(__assign({}, boxStyle), { minWidth: dropdownWidth, maxWidth: dropdownWidth });
    }
    var scheduleUpdateRef = useScheduleUpdate(updateOnChildrenChange ? __spread([boxChildren], updateDeps) : updateDeps);
    var dropdownTrigger = useMemo(function () {
        if (disabled) {
            return "empty";
        }
        var innerTrigger = trigger === "hover" ? HoverTrigger : ClickTrigger;
        // 自定义 Trigger
        if (trigger && typeof trigger !== "string") {
            return trigger;
        }
        return [
            innerTrigger,
            {
                onClose: onClose,
                onOpen: onOpen,
            },
        ];
    }, [disabled, onClose, onOpen, trigger]);
    return (React.createElement(Popover, { trigger: dropdownTrigger, visible: isOpened, onVisibleChange: setIsOpened, placement: placement, placementOffset: placementOffset, closeOnScroll: closeOnScroll, escapeWithReference: escapeWithReference, popupContainer: popupContainer, destroyOnClose: destroyOnClose, openDelay: openDelay, closeDelay: closeDelay, overlayClassName: overlayClassName, overlayStyle: overlayStyle, flipped: flipped, positionFixed: positionFixed, overlay: function (_a) {
            var scheduleUpdate = _a.scheduleUpdate;
            scheduleUpdateRef.current = scheduleUpdate;
            return disabled ? null : (React.createElement(DropdownBox, { className: boxClassName, style: boxStyle, onClick: clickClose ? close : null, adaptable: boxAdaptable, footer: footer, preventMouseDown: preventMouseDown }, boxChildren));
        } },
        React.createElement("div", __assign({ ref: mergeRefs(ref, dropdownRef), className: classNames(classPrefix + "-dropdown", className, (_a = {},
                _a["size-" + (size === "full" || size === "auto" ? size + "-width" : size)] = size,
                _a["is-expanded"] = isOpened,
                _a["is-disabled"] = disabled,
                _a)), style: style, tabIndex: disabled ? -1 : 0, onKeyDown: function (event) {
                if (onKeyDown(event, { open: isOpened }) === false) {
                    return;
                }
                switch (event.key) {
                    case KeyMap.Space:
                    case KeyMap.Enter:
                        if (!isOpened) {
                            setIsOpened(true);
                            onOpen();
                        }
                        if (isOpened) {
                            close();
                        }
                        break;
                    case KeyMap.Up:
                    case KeyMap.Down:
                        event.preventDefault();
                        if (!isOpened) {
                            setIsOpened(true);
                            onOpen();
                        }
                        break;
                    case KeyMap.Esc:
                        if (isOpened) {
                            close();
                        }
                        break;
                }
            } }, getARIAProps(restProps), mergeEventProps(restProps, {
            onClick: function (event) {
                event.preventDefault();
                event.stopPropagation();
                if (disabled) {
                    return;
                }
                if (!isOpened) {
                    onOpen();
                    setIsOpened(true);
                }
                else if (stayedOpen) {
                    onClose();
                    setIsOpened(false);
                }
            },
            onMouseEnter: function () {
                setHover(true);
            },
            onMouseLeave: function () {
                setHover(false);
            },
            onFocus: onFocus,
            onBlur: onBlur,
        })), appearance === "pure" ? (button) : (React.createElement("div", { className: classNames(classPrefix + "-dropdown__header", headerClassName) },
            React.createElement("div", { className: classPrefix + "-dropdown__value" }, button),
            clearable && !disabled && hover ? (React.createElement(Icon, { type: "dismiss", onClick: function (e) {
                    e.stopPropagation();
                    onClear(e);
                } })) : (icon && React.createElement(Icon, { type: icon })))))));
}, {
    Footer: createRocket("DropdownFooter", "div.@{prefix}-dropdown-box__footer"),
});
Dropdown.displayName = "Dropdown";
//# sourceMappingURL=Dropdown.js.map